import type { ClientPollCreateRequest } from '@DigitaleDoerfer/digitale-doerfer-api';
import { type FC, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import { Button, IconButton, Text } from 'react-native-paper';

import { CloseIcon, EditIcon } from '@/assets';
import { usePopup } from '@/features/popup/provider/usePopup';
import { PollCard } from '@/features/post-editor/components/poll/PollCard';
import { PollForm } from '@/features/post-editor/components/poll/PollForm';
import type { Poll } from '@/features/post-editor/types/poll';
import { LocalizationNameSpace } from '@/localization/types/localizationTypes';
import { isEmptyOrNull } from '@/shared/utils/string';
import { useAppTheme } from '@/styles/hooks/useAppTheme';
import type { AppTheme } from '@/styles/theme';

const makeStyles = (theme: AppTheme) =>
  StyleSheet.create({
    container: { padding: 12 },
    remove: {
      position: 'absolute',
      width: 26,
      height: 26,
      top: -6,
      right: 0,
      zIndex: 10
    },
    answers: { gap: 10 },
    answer: {
      borderWidth: 1,
      borderColor: theme.colors.primary,
      color: theme.colors.primary,
      padding: 10,
      textAlign: 'center',
      borderRadius: 100
    },
    footer: { alignItems: 'center' },
    note: { textAlign: 'center' }
  });

type Props = {
  poll?: Poll;
  showEdit: boolean;
  onShowEdit: (show: boolean) => void;
  onChange: (poll?: ClientPollCreateRequest) => void;
  onDelete: (id?: string) => void;
};

export const PollPreview: FC<Props> = ({
  poll,
  showEdit,
  onShowEdit,
  onChange,
  onDelete
}) => {
  const theme = useAppTheme();
  const styles = useMemo(() => makeStyles(theme), [theme]);
  const { t } = useTranslation(LocalizationNameSpace.POST_EDITOR, {
    keyPrefix: 'components.poll.preview'
  });

  const { showAlert } = usePopup();
  const canEdit = isEmptyOrNull(poll?.id);

  const handleDelete = useCallback(() => {
    showAlert({
      dismissable: true,
      labels: t('alerts.delete', { returnObjects: true }),
      onConfirm: () =>
        isEmptyOrNull(poll?.id) ? onChange() : onDelete(poll?.id)
    });
  }, [onChange, onDelete, poll?.id, showAlert, t]);

  const handleClose = useCallback(
    (isDirty: boolean = false, callBack: () => void) => {
      if (isDirty)
        showAlert({
          dismissable: true,
          labels: t('alerts.closeUnsaved', { returnObjects: true }),
          onConfirm: () => {
            onShowEdit(false);
            callBack();
          }
        });
      else {
        onShowEdit(false);
        callBack();
      }
    },
    [onShowEdit, showAlert, t]
  );

  const handleChange = useCallback(
    (updatedPoll?: ClientPollCreateRequest) => {
      if (!updatedPoll) return;

      onChange(updatedPoll);
      onShowEdit(false);
    },
    [onChange, onShowEdit]
  );

  return (
    <>
      {poll && (
        <View style={styles.container}>
          <PollCard
            poll={poll}
            footer={
              canEdit && (
                <View style={styles.footer}>
                  <Button
                    icon={({ color, size }) => (
                      <EditIcon fill={color} width={size} height={size} />
                    )}
                    onPress={() => onShowEdit(true)}
                    mode="text"
                  >
                    {t('edit')}
                  </Button>
                  <Text variant="labelMedium" style={styles.note}>
                    {t('note')}
                  </Text>
                </View>
              )
            }
          >
            <View style={styles.answers}>
              {poll.optionTexts?.map(({ optionText }) => (
                <Text
                  key={optionText}
                  variant="labelLarge"
                  style={styles.answer}
                >
                  {optionText}
                </Text>
              ))}
            </View>
          </PollCard>
          <IconButton
            mode="contained"
            style={styles.remove}
            size={16}
            icon={({ color, size }) => (
              <CloseIcon fill={color} width={size} height={size} />
            )}
            theme={{
              colors: {
                primary: theme.colors.onPrimary,
                surfaceVariant: theme.colors.onSurfaceVariant
              }
            }}
            onPress={handleDelete}
          />
        </View>
      )}
      {canEdit && (
        <PollForm
          poll={poll}
          isVisible={showEdit}
          onChange={handleChange}
          onClose={handleClose}
        />
      )}
    </>
  );
};
